import { cn, TokenIcon, type TokenIconSymbol, Typography } from "@kyan/ui";

import { getPerpConstraints } from "#/config/orders";
import { useMarketIndexPrice } from "#/store/app/indexPrices/marketIndexPrice";
import {
  formatInstrumentName,
  getOptionInstrumentProperties,
  getPerpInstrumentProperties,
  isOptionInstrument,
} from "#/utils/instrument.utils";
import { toDirection, toSide } from "#/utils/marketActionConverter";
import { formatCurrency } from "#/utils/valueFormatters";

import { EstimatedExecPrice } from "../EstimatedExecPrice/EstimatedExecPrice";
import { SizeDisplay } from "../SizeDisplay/SizeDisplay";
import { TradeFee } from "../TradeFee/TradeFee";
import { Price } from "./components/Price";

interface InstrumentSummaryProps {
  size: string;
  isBuy: boolean;
  bestPrice?: number;
  className?: string;
  limitPrice?: number;
  netPrice?: number;
  isMarketOrder?: boolean;
  instrumentName: string;
}

export const InstrumentSummary: React.FC<InstrumentSummaryProps> = ({
  size,
  isBuy,
  bestPrice,
  className,
  limitPrice,
  netPrice,
  isMarketOrder = false,
  instrumentName,
}) => {
  const { marketIndexPrice } = useMarketIndexPrice();

  const isOption = isOptionInstrument(instrumentName);
  const { underlying } = isOption
    ? getOptionInstrumentProperties(instrumentName)
    : getPerpInstrumentProperties(instrumentName);
  const { sizeDisplayPrecision } = getPerpConstraints(underlying);

  const shouldRenderBestPrice = isOption && bestPrice !== undefined;
  const shouldRenderMarketPrice = !isOption;

  return (
    <div
      className={cn(
        "bg-buttonBg dark:bg-buttonFill grid gap-2 rounded-md p-2",
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-textSecondary/10 flex rounded">
            <TokenIcon
              size="sm"
              className="text-textPrimary h-7 w-10"
              symbol={underlying.toLowerCase() as TokenIconSymbol}
            />
            <div className="border-divider border-r" />
            <Typography
              medium
              grotesk
              size={12}
              className={cn(
                "flex h-7 w-10 items-center justify-center capitalize",
                {
                  "text-success": isBuy,
                  "text-error": !isBuy,
                },
              )}
            >
              {toDirection(isBuy)}
            </Typography>
          </div>

          <div className="grid gap-0.5">
            <Typography primary size={14}>
              {formatInstrumentName(instrumentName)}
            </Typography>

            {shouldRenderBestPrice && (
              <Typography size={10}>
                Best{" "}
                <Typography className="capitalize">{toSide(isBuy)}</Typography>{" "}
                {formatCurrency(bestPrice)}
              </Typography>
            )}

            {shouldRenderMarketPrice && (
              <Typography size={10}>
                Market price {formatCurrency(marketIndexPrice)}
              </Typography>
            )}
          </div>
        </div>
        <SizeDisplay
          value={Number(size)}
          className="font-grotesk"
          suffix={isOption ? "Qty" : undefined}
          underlying={isOption ? undefined : underlying}
          precision={isOption ? -1 : sizeDisplayPrecision}
        />
      </div>

      {limitPrice && <Price price={limitPrice} label="Limit price" />}
      {netPrice && <Price price={netPrice} label="Net price" />}

      {isMarketOrder && (
        <>
          <EstimatedExecPrice
            type="market"
            kind={isOption ? "options" : "perps"}
          />
          <TradeFee instrumentType={isOption ? "options" : "perps"} />
        </>
      )}
    </div>
  );
};
